import React, { useState, useEffect, useRef } from 'react';
import { QADatabaseInfo } from '../../types';
import { getQADatabase, uploadQADatabase } from '../../services/api';
import './QASection.css';

interface QASectionProps {
  projectId: string;
}

const QASection: React.FC<QASectionProps> = ({ projectId }) => {
  const [qaInfo, setQaInfo] = useState<QADatabaseInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadQADatabase = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getQADatabase(projectId);
      setQaInfo(response);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load Q&A database');
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadQADatabase();
  }, [projectId]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only CSV files are accepted');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await uploadQADatabase(projectId, file);
      setSuccess(`Successfully uploaded ${result.record_count} Q&A pairs`);
      await loadQADatabase();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];

    if (!file || !file.name.toLowerCase().endsWith('.csv')) {
      setError('Only CSV files are accepted');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await uploadQADatabase(projectId, file);
      setSuccess(`Successfully uploaded ${result.record_count} Q&A pairs`);
      await loadQADatabase();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="qa-section">
      <div className="section-header">
        <h3>💬 Q&A Database</h3>
        <p className="section-description">
          Upload a CSV file containing previously answered questions and their responses.
          This helps the system provide consistent answers to similar questions.
        </p>
      </div>

      {/* Upload Area */}
      <div
        className={`upload-zone ${isUploading ? 'uploading' : ''}`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => !isUploading && fileInputRef.current?.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          accept=".csv"
          onChange={handleFileSelect}
          hidden
        />
        {isUploading ? (
          <div className="upload-status">
            <span className="spinner">⏳</span>
            <span>Uploading Q&A database...</span>
          </div>
        ) : (
          <div className="upload-prompt">
            <span className="upload-icon">📋</span>
            <span>Drag & drop your Q&A CSV file here, or click to browse</span>
          </div>
        )}
      </div>

      {/* Success Message */}
      {success && <div className="success-message">✅ {success}</div>}

      {/* Error Message */}
      {error && <div className="error-message">⚠️ {error}</div>}

      {/* CSV Format Hint */}
      <div className="format-hint">
        <h4>Expected CSV Format:</h4>
        <pre>
{`Question,Answer,Category
"What is your password policy?","We require minimum 12 characters with complexity...",Security
"Do you encrypt data at rest?","Yes, all data is encrypted using AES-256...",Data Protection`}
        </pre>
      </div>

      {/* Current Database Status */}
      {isLoading ? (
        <div className="loading">Loading Q&A database info...</div>
      ) : qaInfo && (
        <div className="qa-status">
          <h4>Current Database</h4>
          {qaInfo.has_database ? (
            <>
              <p className="record-count">📊 {qaInfo.record_count} Q&A pairs loaded</p>
              {qaInfo.preview.length > 0 && (
                <div className="preview">
                  <h5>Preview (first {qaInfo.preview.length} records):</h5>
                  <table className="preview-table">
                    <thead>
                      <tr>
                        {Object.keys(qaInfo.preview[0]).slice(0, 3).map(key => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {qaInfo.preview.map((row, idx) => (
                        <tr key={idx}>
                          {Object.values(row).slice(0, 3).map((val, i) => (
                            <td key={i}>{String(val).substring(0, 100)}{String(val).length > 100 ? '...' : ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <p className="empty-state">No Q&A database uploaded yet</p>
          )}
        </div>
      )}
    </div>
  );
};

export default QASection;

